import React, { useState } from "react";
import config from "../../config/config";
import { ThemeProvider } from "onnit-ui/lib/themes/styled";
import useOnnitContext from "onnit-ui/lib/hooks/useOnnitContext";
import usePageData from "../../hooks/usePageData";
import GlobalStyle from "onnit-ui/lib/themes/GlobalStyle";
import theme from "onnit-ui/lib/themes/light";
import { Img, Picture } from "onnit-ui/lib/image";
import Grid from "onnit-ui/lib/module/grid/12/Grid";
import Box from "onnit-ui/lib/box/Box";
import Text, { MultiColumnText, Sup } from "onnit-ui/lib/text";
import SupplementFacts from "../../components/common/SupplementFacts";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import ProductImageGrid from "onnit-ui/lib/module/ProductImageGrid";

import MediaHero01 from "onnit-ui/lib/module/MediaHero01";
import Embed from "onnit-ui/lib/video/Embed";
import MediaCollage02 from "onnit-ui/lib/module/MediaCollage02";
import MediaCollage01 from "onnit-ui/lib/module/MediaCollage01";
import CopyDisplay02 from "onnit-ui/lib/module/CopyDisplay02";
import UpSell from "../../components/common/UpSell";
import Video from "onnit-ui/lib/video/Video";
import BgImage from "onnit-ui/lib/image/BgImage";
import ingredients from "./ingredients";
import MediaContentShowcase, { MediaContentShowcaseItem } from "onnit-ui/lib/module/MediaContentShowcase";
import { Testimonial } from "onnit-ui/lib/module/testimonials";

function Page() {
    const onnitContext = useOnnitContext();
    const { pageData, findProduct } = usePageData();
    const [currentPID, setCurrentPID] = useState<number | undefined>();

    if (!pageData || !onnitContext) return null;

    const IMG_URL = `${onnitContext.url.cdn.static_images}/product-page/${pageData.slug}`;
    const VID_URL = `${onnitContext.url.cdn.static}/images/product-page/${pageData.slug}`;
    const IMG_PARAMS = config.IMG_PARAMS;
    const orange = "#71C6AC";

    // Upsell Flavors
    const PEACH = findProduct(8546);
    const TROPICAL = findProduct(8549);
    const PEACH_24_BOTTLES = findProduct(8740);
    const TROPICAL_24_BOTTLES = findProduct(8739);
    //The "You save 20%" is based on the total of a 6ct package x4
    const PEACH_24_DISCOUNT = PEACH ? (parseFloat(PEACH.price_msrp_raw) * 4 * 0.2).toFixed(2) : null;
    const TROPICAL_24_DISCOUNT = TROPICAL ? (parseFloat(TROPICAL.price_msrp_raw) * 4 * 0.2).toFixed(2) : null;

    const isDisruptiveSale = window.onnit_context.disruptive_sale.is_active;

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />
                <DefaultProductPageTop
                    productNameTransform={(name) => {
                        return name.replace(/Alpha BRAIN® Focus Shot - /gi, "");
                    }}
                    onProductChange={(product) => {
                        setCurrentPID(product.id);
                    }}
                >
                    <ProductImageGrid variant="layout3">
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[`${IMG_URL}/BuyBox-1.jpg${IMG_PARAMS}`]}
                            alt="Alpha brain focus shot peach flavor bottle."
                        />
                        <Img
                            src={`${IMG_URL}/BuyBox-2.jpg${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="Alpha brain focus shot tropical flavor bottle."
                            width={920}
                            height={920}
                        />
                        <Img
                            src={`${IMG_URL}/BuyBox-3.jpg${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="Girl wake boarding."
                            width={1888}
                            height={848}
                        />
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[`${IMG_URL}/BuyBox-4.jpg${IMG_PARAMS}`]}
                            alt="Alpha brain focus shot tropical flavor on wake board."
                        />
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[`${IMG_URL}/BuyBox-5.jpg${IMG_PARAMS}`]}
                            alt="Woman drinking alpha brain focus shot tropical flavor."
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <div className="hide-ie">
                    <Box backgroundImage="linear-gradient(#44BDCD, #38B4A0)" py={[6, 6, 8, 10]} mt={[0, 0, 8, 10]}>
                        <MediaHero01
                            mb={[6, 6, 8, 10]}
                            media={
                                <Picture
                                    lazyLoad={true}
                                    width={776}
                                    height={776}
                                    src={[`${IMG_URL}/MediaHero-1.jpg${IMG_PARAMS}`]}
                                    alt="Woman placing alpha brain focus shot bottle on boat."
                                />
                            }
                            headline={
                                <Text as="h2">
                                    <Text typeStyle="headlineHero01" color="white">
                                        Take Your
                                    </Text>
                                    <Text typeStyle="headlineHero01" color="white">
                                        Best Shot
                                    </Text>
                                </Text>
                            }
                        />

                        <Grid px={[4, 4, 5]}>
                            <Box gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / span 9"]}>
                                <Text typeStyle="copyHero01" color="white" as="p">
                                    Alpha BRAIN
                                    <Sup variant="reg" /> Focus Shot isn’t a quick caffeine fix or false energy in a
                                    tiny bottle. Designed to help you get into flow state (that feeling of being “in the
                                    zone”), support a good mood, and assist in managing stress, Alpha BRAIN
                                    <Sup variant="reg" /> Focus Shot is the energy shot for those who need to think fast
                                    and clearly while keeping cool under pressure.
                                    <Sup variant="dagger" />
                                </Text>
                            </Box>
                        </Grid>

                        <Grid>
                            <Box gridColumn={["1 / -1", "1/-1", "1/-1", "3 / span 8"]} px={4} mt={[6, 6, 8, 10]}>
                                <Embed
                                    title="Alpha brain focus shot product video"
                                    src="https://player.vimeo.com/video/574554610"
                                />
                            </Box>
                        </Grid>
                    </Box>

                    <CopyDisplay02 my={[6, 6, 6, 8]}>
                        <Text as="h2" typeStyle="copyDisplay01">
                            Available In Two Flavors
                        </Text>
                    </CopyDisplay02>

                    {PEACH_24_BOTTLES && (
                        <UpSell
                            textColor="black"
                            buttonColor="black"
                            buttonTextColor="white"
                            pid={PEACH_24_BOTTLES.id}
                            price={PEACH_24_BOTTLES.price}
                            msrp={PEACH_24_BOTTLES.price_msrp}
                            inStock={PEACH_24_BOTTLES.in_stock}
                            showPercentageSaved={true}
                            img={
                                <Img
                                    src={`${IMG_URL}/Flavor-Peach.jpg${IMG_PARAMS}`}
                                    alt={PEACH_24_BOTTLES.name}
                                    width={920}
                                    height={920}
                                />
                            }
                            title={<Text typeStyle="copyDisplay01">Peach</Text>}
                            subtext={
                                <>
                                    24 bottles
                                    {!isDisruptiveSale && (
                                        <span style={{ color: "#E00000", marginLeft: "5px" }}>
                                            ` Save $${PEACH_24_DISCOUNT}`
                                        </span>
                                    )}
                                </>
                            }
                            description={
                                <>
                                    Similar in taste to the Alpha BRAIN
                                    <Sup variant="reg" /> Instant powder mix that preceded it, Focus Shot Peach offers a
                                    ripe, country-fresh flavor (and no fuzz).
                                    {!isDisruptiveSale && (
                                        <>
                                            <br />
                                            <br />
                                            <Text color="#E00000" typeStyle="copyHero02">
                                                {" "}
                                                Save 20% when you buy a 24 ct{" "}
                                            </Text>
                                        </>
                                    )}
                                </>
                            }
                        />
                    )}

                    {TROPICAL_24_BOTTLES && (
                        <UpSell
                            textColor="black"
                            buttonColor="black"
                            buttonTextColor="white"
                            pid={TROPICAL_24_BOTTLES.id}
                            price={TROPICAL_24_BOTTLES.price}
                            msrp={TROPICAL_24_BOTTLES.price_msrp}
                            inStock={TROPICAL_24_BOTTLES.in_stock}
                            showPercentageSaved={true}
                            img={
                                <Img
                                    src={`${IMG_URL}/Flavor-Tropical.jpg${IMG_PARAMS}`}
                                    alt={TROPICAL_24_BOTTLES.name}
                                    width={920}
                                    height={920}
                                />
                            }
                            title={<Text typeStyle="copyDisplay01">Tropical</Text>}
                            subtext={
                                <>
                                    24 bottles
                                    {!isDisruptiveSale && (
                                        <span style={{ color: "#E00000", marginLeft: "5px" }}>
                                            ` Save $${TROPICAL_24_DISCOUNT}`
                                        </span>
                                    )}
                                </>
                            }
                            description={
                                <>
                                    This fruity, punchy flavor tastes like it should be served under a thatched roof on
                                    a beach while a cool breeze blows through your hair… even if you’re really sitting
                                    in your office downtown.
                                    {!isDisruptiveSale && (
                                        <>
                                            <br />
                                            <br />
                                            <Text color="#E00000" typeStyle="copyHero02">
                                                {" "}
                                                Save 20% when you buy a 24 ct{" "}
                                            </Text>
                                        </>
                                    )}
                                </>
                            }
                        />
                    )}

                    <Box py={[8, 8, 10, 12]}>
                        <BgImage
                            position="relative"
                            backgroundSize={["cover", "contain"]}
                            backgroundPosition={["center", "center -10%", "center -10%", "center 25%"]}
                            mt={["-4%", "-4%", "-7%"]}
                            src={[`${IMG_URL}/background-pattern.svg?q=40`]}
                        >
                            <MediaCollage02
                                item1={
                                    <Img
                                        src={`${IMG_URL}/MediaCollage02-1.jpg${IMG_PARAMS}`}
                                        alt="Ashley Kidd pushing sled machine."
                                        width={448}
                                        height={336}
                                    />
                                }
                                item2={
                                    <Img
                                        src={`${IMG_URL}/MediaCollage02-2.jpg${IMG_PARAMS}`}
                                        alt="Ashlhey Kidd getting ready to wake board."
                                        width={688}
                                        height={688}
                                    />
                                }
                                item3={
                                    <Img
                                        src={`${IMG_URL}/MediaCollage02-3.jpg${IMG_PARAMS}`}
                                        alt="Ashley Kidd exercising."
                                        width={1168}
                                        height={876}
                                    />
                                }
                            />
                        </BgImage>
                    </Box>

                    <Box display="grid" gridTemplateColumns="repeat(12, 1fr)">
                        <Box gridColumn={["1 / -1", "2 / -1"]} gridRow={1} px={4}>
                            <Text as="h2" typeStyle="headlineHero01" mb={[5, 8]}>
                                How <br />
                                Alpha BRAIN
                                <Sup variant="reg" /> <br />
                                Focus Shot <br />
                                Works
                            </Text>
                        </Box>

                        <Box
                            bg={["transparent", "rgba(198,177,208,0.2)"]}
                            gridColumn={["1 / -1", "4 / span 8"]}
                            gridRow={2}
                            p={[4, 4, 5]}
                        >
                            <Text as="p" typeStyle="copyHero02" fontWeight="regular">
                                As with Alpha BRAIN
                                <Sup variant="reg" /> and Alpha BRAIN
                                <Sup variant="reg" /> Instant, Focus Shot helps you achieve flow state—that feeling of
                                being “in the zone.”
                                <Sup variant="dagger" />
                                <br />
                                <br />
                                With the addition of new ingredients, including a low dose of plant-based caffeine, the
                                patented Cognizin
                                <Sup variant="reg" /> citicoline, and the adaptogenic herb ashwagandha (KSM-66
                                <Sup variant="reg" />
                                ), Focus Shot can further support focus and attention while promoting a positive mood
                                state and helping to manage mental stress.
                                <Sup variant="dagger" />
                                <br />
                                <br />
                                The result is a formula that promotes a sense of relaxed alertness.
                                <Sup variant="dagger" />
                            </Text>
                        </Box>
                    </Box>

                    <MediaCollage01
                        py={[8, 8, 10, 12]}
                        item1={
                            <Img
                                src={`${IMG_URL}/MediaCollage01-1.jpg${IMG_PARAMS}`}
                                alt="Ashley Kidd drinking Alpha Brain focus shot."
                                width={448}
                                height={692}
                            />
                        }
                        item2={
                            <Img
                                src={`${IMG_URL}/MediaCollage01-2.jpg${IMG_PARAMS}`}
                                alt="Ashley Kidd posing for camera."
                                width={448}
                                height={692}
                            />
                        }
                        item3={
                            <Video autoPlay loop muted playsInline placeholder={[620, 840]} aria-hidden="true">
                                <source
                                    src={`${VID_URL}/MediaCollage01-3.mp4`}
                                    type="video/mp4"
                                    title="Ashley Kidd with various action shots."
                                />
                            </Video>
                        }
                    />

                    <Box bg="black" py={[6, 6, 8]}>
                        <Grid>
                            <Box px={4} py={6} gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / 12"]}>
                                <Text as="h2" typeStyle="headlineHero02" color="white" fontSize={[5, 5, 6, 8, 10]}>
                                    Alpha BRAIN
                                    <Sup variant="reg" /> Focus Shot Key Ingredients
                                </Text>
                            </Box>
                        </Grid>
                        <MediaContentShowcase
                            items={ingredients.map((x: MediaContentShowcaseItem) => {
                                let copy = { ...x };
                                copy.image = `${IMG_URL}/ingredients/${x.image}`;
                                return copy;
                            })}
                            textColor="white"
                            activeColor={orange}
                            bgMedia={
                                <Img
                                    src={`${IMG_URL}/ingredients/Ingredients-BG-Banner.jpg${IMG_PARAMS}`}
                                    alt=""
                                    aria-hidden="true"
                                    width={1848}
                                    height={518}
                                />
                            }
                        />
                    </Box>

                    <Box backgroundImage="linear-gradient(#44BDCD, #38B4A0)" py={[6, 6, 8]}>
                        <Testimonial
                            imgSrc={`${IMG_URL}/testimonial.png`}
                            quote='The Alpha BRAIN<span class="oui-sup">®</span> Focus Shot helped me focus in on exactly what I was doing as soon as I was out on the water.'
                            author="Ashley Kidd"
                            title="5X World Champion Wake Surfer"
                        />
                    </Box>

                    <Box maxWidth={944} mx="auto" my={[6, 6, 8]}>
                        <Img
                            src={`${IMG_URL}/Landscape-1.jpg${IMG_PARAMS}`}
                            alt="Ashley Kidd on ski erg."
                            width={1888}
                            height={1062}
                        />
                    </Box>

                    <CopyDisplay02>
                        <Text as="h2" typeStyle="copyDisplay02" mb={[6, 6, 6, 8]}>
                            What’s The Difference Between Alpha BRAIN
                            <Sup variant="reg" /> Focus Shot and Original Alpha BRAIN
                            <Sup variant="reg" />?
                        </Text>
                        <MultiColumnText>
                            <Text as="p" typeStyle="copyHero02" color="grays.4" mb={4}>
                                Alpha BRAIN
                                <Sup variant="reg" /> was originally available only in capsules. We soon learned that
                                many customers didn’t want to swallow pills or carry a bottle around with them, so we
                                created Alpha BRAIN
                                <Sup variant="reg" /> Instant, a powdered drink mix that allowed people to enjoy Alpha
                                BRAIN
                                <Sup variant="reg" /> as a beverage. Still, some folks needed a faster, simpler option.
                            </Text>
                            <Text as="p" typeStyle="copyHero02" color="grays.4" mb={4}>
                                We went back to the drawing board to create Focus Shot, the most convenient way to
                                consume Alpha BRAIN
                                <Sup variant="reg" /> yet. Boasting the addition of some new ingredients, and available
                                in two delicious flavors, Focus Shot is the perfect grab-and-go energy drink, and small
                                enough to fit in your pocket.
                            </Text>
                            <Text as="p" typeStyle="copyHero02" color="grays.4" mb={4}>
                                No pesky capsules to swallow. No messy powders to mix. Just pop the top and down it in
                                one shot!
                            </Text>
                        </MultiColumnText>
                    </CopyDisplay02>
                </div>

                {pageData.suggested_use && pageData.supplement_facts && (
                    <UsageAndDisclaimers supplementFacts={<SupplementFacts currentPID={currentPID} />} />
                )}
            </>
        </ThemeProvider>
    );
}

export default Page;
